import React, { useEffect, useRef, useState } from 'react';
import type { TaskPriority } from '@dreamai/types';
import BottomSheet from './BottomSheet';
import MemberAvatar from '../chat/reminders/MemberAvatar';
import { ReactComponent as CalendarIcon } from '../../icons/calendar-check-02.svg';
import { ReactComponent as UserPlusIcon } from '../../icons/user-plus.svg';
import './task-composer-sheet.scss';

const PRIORITIES: readonly TaskPriority[] = ['high', 'medium', 'low'];
export const MAX_TASK_DESCRIPTION = 500;

export interface TaskComposerValues {
  description: string;
  /** ISO date (YYYY-MM-DD) */
  dueDate?: string;
  priority?: TaskPriority;
  assigneeUserId?: string;
}

export interface TaskComposerAssignee {
  userId: string;
  /** Text shown in the picker and, once chosen, on the Assign chip. */
  label: string;
  /** Name used for the avatar initials; defaults to `label`. */
  name?: string;
  /** Invited but not yet joined; rendered under `labels.awaitingGroup`. */
  awaiting?: boolean;
}

export interface TaskComposerLabels {
  title: string;
  description: string;
  descriptionPlaceholder: string;
  /** Due-date chip before a date is chosen. */
  dueDate: string;
  /** Assign chip before someone is chosen. */
  assign: string;
  /** Picker option that clears the assignee. */
  unassigned: string;
  awaitingGroup?: string;
  /** Priority chip before a priority is chosen. */
  priority: string;
  /** Picker option that clears the priority. */
  noPriority: string;
  /** Chip text once a priority is chosen, e.g. "High priority". */
  priorities: Record<TaskPriority, string>;
  submit: string;
  submitting: string;
}

interface TaskComposerSheetProps {
  open: boolean;
  onClose: () => void;
  labels: TaskComposerLabels;
  /** Assignable people; the Assign chip is hidden when empty. */
  assignees: TaskComposerAssignee[];
  /**
   * Resolves with an error message to keep the sheet open and show it, or
   * with nothing once the task has been handed off (the sheet then closes).
   */
  onSubmit: (values: TaskComposerValues) => Promise<string | void>;
  /** Visible lines of the task field. */
  descriptionRows?: number;
  /** BCP 47 tag used to render the chosen due date. */
  locale?: string;
  className?: string;
  testIdPrefix?: string;
}

const formatDueDate = (iso: string, locale: string): string => {
  const [y, m, d] = iso.split('-').map(Number);
  if (!y || !m || !d) return iso;
  const parts = new Intl.DateTimeFormat(locale, {
    weekday: 'short',
    day: 'numeric',
    month: 'short',
    year: 'numeric',
  }).formatToParts(new Date(y, m - 1, d));
  const part = (type: Intl.DateTimeFormatPartTypes) =>
    parts.find((p) => p.type === type)?.value ?? '';
  return `${part('weekday')}, ${part('day')} ${part('month')} ${part('year')}`;
};

/**
 * Bottom-sheet task composer shared by the guest list view and the
 * authenticated mobile Reminders "New" button (Figma 5264:928 / 5264:1125):
 * an underlined task field followed by chips for due date, assignee and
 * priority. Each chip wraps the native control so the OS picker opens on tap.
 * Enter submits, Shift+Enter adds a line.
 */
const TaskComposerSheet: React.FC<TaskComposerSheetProps> = ({
  open,
  onClose,
  labels,
  assignees,
  onSubmit,
  descriptionRows = 1,
  locale = 'en-US',
  className,
  testIdPrefix = 'task-composer',
}) => {
  const inputRef = useRef<HTMLTextAreaElement>(null);
  const [description, setDescription] = useState('');
  const [dueDate, setDueDate] = useState('');
  const [priority, setPriority] = useState<TaskPriority | ''>('');
  const [assigneeUserId, setAssigneeUserId] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!open) return;
    setDescription('');
    setDueDate('');
    setPriority('');
    setAssigneeUserId('');
    setError(null);
    setSubmitting(false);
    const id = window.setTimeout(() => inputRef.current?.focus(), 50);
    return () => window.clearTimeout(id);
  }, [open]);

  const trimmed = description.trim();
  const canSubmit =
    trimmed.length > 0 && trimmed.length <= MAX_TASK_DESCRIPTION && !submitting;

  const submit = async (e?: React.FormEvent) => {
    e?.preventDefault();
    if (!canSubmit) return;
    setSubmitting(true);
    setError(null);
    try {
      const message = await onSubmit({
        description: trimmed,
        ...(dueDate ? { dueDate } : {}),
        ...(priority ? { priority } : {}),
        ...(assigneeUserId ? { assigneeUserId } : {}),
      });
      if (message) {
        setError(message);
        return;
      }
      onClose();
    } finally {
      setSubmitting(false);
    }
  };

  const accepted = assignees.filter((a) => !a.awaiting);
  const awaiting = assignees.filter((a) => a.awaiting);
  const assignee = assignees.find((a) => a.userId === assigneeUserId);
  const ids = {
    description: `${testIdPrefix}-description`,
    due: `${testIdPrefix}-due`,
    assignee: `${testIdPrefix}-assignee`,
    priority: `${testIdPrefix}-priority`,
  };

  return (
    <BottomSheet
      open={open}
      onClose={onClose}
      title={labels.title}
      className={`task-composer-sheet${className ? ` ${className}` : ''}`}
    >
      <form
        className='bottom-sheet-form task-composer-form'
        onSubmit={submit}
        data-testid={`${testIdPrefix}-form`}
      >
        <div className='bottom-sheet-field'>
          <label className='bottom-sheet-label' htmlFor={ids.description}>
            {labels.description}
          </label>
          <textarea
            id={ids.description}
            ref={inputRef}
            className={`task-composer-description${descriptionRows > 1 ? ' is-tall' : ''}`}
            rows={descriptionRows}
            value={description}
            maxLength={MAX_TASK_DESCRIPTION}
            placeholder={labels.descriptionPlaceholder}
            onChange={(e) => setDescription(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === 'Enter' && !e.shiftKey) {
                e.preventDefault();
                void submit();
              }
            }}
            aria-invalid={error ? 'true' : undefined}
            autoComplete='off'
          />
        </div>

        <div className='task-composer-chips'>
          <label
            className={`task-composer-chip${dueDate ? ' is-set' : ''}`}
            htmlFor={ids.due}
          >
            <CalendarIcon className='task-composer-chip-icon' aria-hidden />
            <span className='task-composer-chip-text'>
              {dueDate ? formatDueDate(dueDate, locale) : labels.dueDate}
            </span>
            <input
              id={ids.due}
              className='task-composer-chip-control'
              type='date'
              value={dueDate}
              aria-label={labels.dueDate}
              onChange={(e) => setDueDate(e.target.value)}
              onClick={(e) => {
                // Open the calendar from anywhere on the chip, not just the icon.
                try {
                  e.currentTarget.showPicker();
                } catch {
                  /* unsupported browser */
                }
              }}
            />
          </label>

          {assignees.length > 0 && (
            <label
              className={`task-composer-chip${assignee ? ' is-set' : ''}`}
              htmlFor={ids.assignee}
            >
              {assignee ? (
                <MemberAvatar
                  name={assignee.name ?? assignee.label}
                  size={24}
                  className='task-composer-chip-avatar'
                />
              ) : (
                <UserPlusIcon className='task-composer-chip-icon' aria-hidden />
              )}
              <span className='task-composer-chip-text'>
                {assignee ? assignee.label : labels.assign}
              </span>
              <select
                id={ids.assignee}
                className='task-composer-chip-control'
                value={assigneeUserId}
                aria-label={labels.assign}
                onChange={(e) => setAssigneeUserId(e.target.value)}
              >
                <option value=''>{labels.unassigned}</option>
                {accepted.map((a) => (
                  <option key={a.userId} value={a.userId}>
                    {a.label}
                  </option>
                ))}
                {awaiting.length > 0 && (
                  <optgroup label={labels.awaitingGroup ?? ''}>
                    {awaiting.map((a) => (
                      <option key={a.userId} value={a.userId}>
                        {a.label}
                      </option>
                    ))}
                  </optgroup>
                )}
              </select>
            </label>
          )}

          <label
            className={`task-composer-chip task-composer-chip--priority${priority ? ` is-set is-${priority}` : ''}`}
            htmlFor={ids.priority}
          >
            <span className='task-composer-chip-dot' aria-hidden />
            <span className='task-composer-chip-text'>
              {priority ? labels.priorities[priority] : labels.priority}
            </span>
            <select
              id={ids.priority}
              className='task-composer-chip-control'
              value={priority}
              aria-label={labels.priority}
              onChange={(e) => setPriority(e.target.value as TaskPriority | '')}
            >
              <option value=''>{labels.noPriority}</option>
              {PRIORITIES.map((p) => (
                <option key={p} value={p}>
                  {labels.priorities[p]}
                </option>
              ))}
            </select>
          </label>
        </div>

        {error && (
          <p className='bottom-sheet-hint warning' role='alert'>
            {error}
          </p>
        )}

        <button
          type='submit'
          className='bottom-sheet-primary'
          disabled={!canSubmit}
          data-testid={`${testIdPrefix}-submit`}
        >
          {submitting ? labels.submitting : labels.submit}
        </button>
      </form>
    </BottomSheet>
  );
};

export default TaskComposerSheet;
